import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export type Member360Tab = 'profile' | 'installments' | 'team';
const TABS: Member360Tab[] = ['profile', 'installments', 'team'];

/**
 * /team?focus=<userId>&member=<userId>&tab=<tab> — the URL is the navigator state, so
 * browser back steps out of drill-downs / closes the sheet and views are deep-linkable.
 */
export function useTeamUrlState() {
  const [params, setParams] = useSearchParams();
  const focus = params.get('focus');
  const member = params.get('member');
  const rawTab = params.get('tab') as Member360Tab | null;
  const tab: Member360Tab = rawTab && TABS.includes(rawTab) ? rawTab : 'profile';

  const update = useCallback(
    (patch: Record<string, string | null>, replace = false) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v === null) next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace },
      );
    },
    [setParams],
  );

  return {
    focus,
    member,
    tab,
    /** Drill into (or back to) a level; `null` = My Team. */
    setFocus: (userId: string | null, keepMember = false) =>
      update(keepMember ? { focus: userId } : { focus: userId, member: null, tab: null }),
    openMember: (userId: string) => update({ member: userId, tab: null }),
    closeMember: () => update({ member: null, tab: null }),
    setTab: (next: Member360Tab) => update({ tab: next === 'profile' ? null : next }, true),
    /** Search jump: show the member's level and open their panel. */
    jumpTo: (focusUserId: string | null, memberUserId: string) =>
      update({ focus: focusUserId, member: memberUserId, tab: null }),
  };
}
